import React, { useEffect, useCallback, useMemo, useRef } from "react";
import {
  Text,
  View,
  Platform,
  ActivityIndicator,
  ScrollView,
  Alert,
  Pressable,
  useWindowDimensions,
  Animated,
  StyleSheet,
} from "react-native";
import { useSelector, shallowEqual } from "react-redux";
import Svg, { Path } from "react-native-svg";

import { LoadingSkeleton, ExpandIcon, CompressIcon, EyeIcon, BoltIcon, ListIcon, ChevronRightIcon, StatusBadge } from "../../components/buildingScreen/utils";

// Components
import CleanNowCard from "../../components/CleanNowCard";
import Building3D from "../../components/3DBuilding/3DBuilding";
import EmptyState from "../../components/EmptyState";
import BuildingScreenHeader from "./BuildingScreenHeader";

// Hooks & Utils
import { useBuildingData } from "../../hooks/useBuildingData";
import { useFormValidation } from "../../hooks/useFormValidation";
import { useFullscreen } from "../../hooks/useFullscreen";
import { useDevicePerformance } from "../../hooks/useDevicePerformance";
import { FORM_TYPES } from "../../constants/firebase";
import { isTodaysForm } from "../../utils/buildingErrorHandling";

const formGroups = [
  {
    key: "preclean",
    title: "Pre-Clean Essentials",
    subtitle: "Complete these first before starting a clean.",
    forms: [
      { label: `${FORM_TYPES.EQUIPMENT} Checklist`, screen: "Equipment", field: "equipmentForm" },
      { label: FORM_TYPES.WEATHER, screen: "Weather", field: "weatherForm" },
      { label: FORM_TYPES.PPE, screen: "PPE", field: "ppeForm" },
    ],
  },
  {
    key: "reporting",
    title: "Reporting & Follow-up",
    subtitle: "Capture issues and scheduled tasks during your walk-through.",
    forms: [
      { label: FORM_TYPES.DEFECTS, screen: "Defects", field: "defectsForm" },
      { label: FORM_TYPES.SCHEDULED_TASKS, screen: "Schedules", field: "schedulesForm" },
    ],
  },
];



export default function BuildingScreen({ navigation }) {
  const { width: screenWidth } = useWindowDimensions();
  const styles = useMemo(() => buildStyles(screenWidth), [screenWidth]);
  const [show3D, setShow3D] = React.useState(screenWidth >= 1024);
  const [showLegend, setShowLegend] = React.useState(false);
  const [lowPerformanceMode, setLowPerformanceMode] = React.useState(false);
  const [hasSeenPerformanceWarning, setHasSeenPerformanceWarning] = React.useState(false);
  const [showFloatingControls, setShowFloatingControls] = React.useState(true);
  const [isControlsPanelOpen, setIsControlsPanelOpen] = React.useState(false);
  const [controlHint, setControlHint] = React.useState("");
  const modelViewerRef = React.useRef(null);
  const hintTimeoutRef = React.useRef(null);
  const controlsHideTimeoutRef = React.useRef(null);
  const viewerButtonScale = useRef(new Animated.Value(1)).current;
  const isWeb = Platform.OS === "web";
  const isCompact = screenWidth < 768;
  const { shouldUseLowPerformanceMode, shouldWarnLargeModels, reason, isSamsungA36Like } =
    useDevicePerformance();
  // Fullscreen state is scoped to the 3D container so Escape and browser UI work natively.
  const {
    isSupported: isFullscreenSupported,
    isFullscreen,
    toggleFullscreen,
  } = useFullscreen(modelViewerRef);

  useEffect(() => {
    if (!isWeb) {
      setShow3D(false);
      return;
    }

    if (screenWidth >= 1024) {
      setShow3D(true);
    }
  }, [isWeb, screenWidth]);

  useEffect(() => {
    if (shouldUseLowPerformanceMode) {
      setLowPerformanceMode(true);
    }
  }, [shouldUseLowPerformanceMode]);

  const { selectedBuilding, refreshVersion } = useSelector(
    (state) => ({
      selectedBuilding: state.buildingList.selectedBuilding,
      refreshVersion: state.buildingList.refreshBuildingVersion,
    }),
    shallowEqual
  );

  const { buildingLoading, dropsLoading, error, buildingData, currentClean, drops, clearError, reload } =
    useBuildingData(selectedBuilding, refreshVersion);

  const userRole = useSelector((state) => state.auth.role);
  const canUpdatePicture = userRole === "admin";

  const siteIsAdhoc = selectedBuilding?.isAdhoc || false;

  const selectedBuildingName = useMemo(
    () =>
      (siteIsAdhoc ? selectedBuilding?.site_name : selectedBuilding?.name) ||
      "Building",
    [siteIsAdhoc, selectedBuilding]
  );

  const modelSiteId = useMemo(() => {
    if (!selectedBuilding) {
      return "";
    }
    return selectedBuilding?.name || selectedBuilding?.site_name || "";
  }, [selectedBuilding]);

  const progressValue = useMemo(
    () => Math.min(Number(buildingData?.currentCleanPercentageCompleted || 0), 1),
    [buildingData]
  );

  const progressPercent = useMemo(
    () => `${Math.round(progressValue * 100)}%`,
    [progressValue]
  );

  const formValidation = useFormValidation(buildingData);

  const currentCleanStatus = useMemo(() => {
    if (progressValue >= 1) {
      return "Completed";
    }

    if (formValidation.canProceedToClean) {
      return "Ready to clean";
    }

    return "Action needed";
  }, [progressValue, formValidation.canProceedToClean]);

  const handleToggle3D = useCallback(() => {
    const nextValue = !show3D;

    if (
      nextValue &&
      isWeb &&
      shouldWarnLargeModels &&
      !hasSeenPerformanceWarning
    ) {
      const openNormally = window.confirm(
        "This 3D model may run slowly on your phone. Tap OK to continue, or Cancel to open in Low Performance Mode."
      );

      if (!openNormally) {
        setLowPerformanceMode(true);
      }
      setHasSeenPerformanceWarning(true);
    }

    if (!nextValue && isFullscreen) {
      toggleFullscreen();
    }

    if (!nextValue) {
      setIsControlsPanelOpen(false);
    }

    setShow3D(nextValue);
  }, [show3D, isWeb, shouldWarnLargeModels, hasSeenPerformanceWarning, isFullscreen, toggleFullscreen]);

  const animateViewerButtonIn = useCallback(() => {
    Animated.spring(viewerButtonScale, {
      toValue: 0.96,
      useNativeDriver: true,
      friction: 7,
      tension: 140,
    }).start();
  }, [viewerButtonScale]);

  const animateViewerButtonOut = useCallback(() => {
    Animated.spring(viewerButtonScale, {
      toValue: 1,
      useNativeDriver: true,
      friction: 7,
      tension: 140,
    }).start();
  }, [viewerButtonScale]);

  const handleToggleFullscreen = useCallback(() => {
    toggleFullscreen();
    setShowFloatingControls(true);
  }, [toggleFullscreen]);

  const handleToggleLegend = useCallback(() => {
    setShowLegend((prev) => !prev);
  }, []);

  const handleToggleLowPerformance = useCallback((nextValue) => {
    if (typeof nextValue === "boolean") {
      setLowPerformanceMode(nextValue);
      return;
    }
    setLowPerformanceMode((prev) => !prev);
  }, []);

  const showHint = useCallback((message) => {
    if (hintTimeoutRef.current) {
      clearTimeout(hintTimeoutRef.current);
    }

    setControlHint(message);
    hintTimeoutRef.current = setTimeout(() => {
      setControlHint("");
    }, 2600);
  }, []);

  useEffect(() => {
    return () => {
      if (hintTimeoutRef.current) {
        clearTimeout(hintTimeoutRef.current);
      }
      if (controlsHideTimeoutRef.current) {
        clearTimeout(controlsHideTimeoutRef.current);
      }
    };
  }, []);

  const scheduleFloatingControlsAutoHide = useCallback(() => {
    if (!isFullscreen || !show3D || isControlsPanelOpen) {
      return;
    }

    if (controlsHideTimeoutRef.current) {
      clearTimeout(controlsHideTimeoutRef.current);
    }

    controlsHideTimeoutRef.current = setTimeout(() => {
      setShowFloatingControls(false);
    }, 4600);
  }, [isFullscreen, show3D, isControlsPanelOpen]);

  const revealFloatingControls = useCallback(() => {
    setShowFloatingControls(true);
    scheduleFloatingControlsAutoHide();
  }, [scheduleFloatingControlsAutoHide]);

  useEffect(() => {
    if (!isFullscreen || !show3D) {
      setShowFloatingControls(true);
      return;
    }

    scheduleFloatingControlsAutoHide();
  }, [isFullscreen, show3D, isControlsPanelOpen, scheduleFloatingControlsAutoHide]);

  const handleToggleControlsPanel = useCallback(() => {
    setIsControlsPanelOpen((prev) => !prev);
    setShowFloatingControls(true);
  }, []);

  const groupedForms = useMemo(() => {
    return formGroups
      .map((group) => {
        const forms = group.forms.filter((item) => {
          if (siteIsAdhoc && ["Defects", "Schedules"].includes(item.screen)) {
            return false;
          }
          return true;
        });

        return {
          ...group,
          forms,
        };
      })
      .filter((group) => group.forms.length > 0);
  }, [siteIsAdhoc]);

  const getFormStatus = useCallback(
    (formItem) => {
      if (formItem.screen === "Weather") {
        return formValidation.missingForms.includes("Weather") ? "Pending" : "Completed";
      }

      if (formItem.screen === "Equipment") {
        return formValidation.missingForms.includes("Equipment") ? "Pending" : "Completed";
      }

      if (formItem.screen === "PPE") {
        const formData = buildingData?.[formItem.field];
        return isTodaysForm(formData) ? "Completed" : "Pending";
      }

      return "Optional";
    },
    [buildingData, formValidation.missingForms]
  );

  const handleErrorRetry = useCallback(() => {
    clearError();
    if (typeof reload === "function") {
      reload();
    }
  }, [clearError, reload]);

  const openForm = useCallback(
    (formType) => {
      navigation.navigate(formType, {
        formType: "pre",
        siteId:
          selectedBuilding?.site_id ||
          selectedBuilding?.site_name ||
          selectedBuildingName,
      });
    },
    [navigation, selectedBuilding, selectedBuildingName]
  );

  const goToCleanAndDropSelection = useCallback(() => {
    if (!formValidation.areFormsToday) {
      const msg = `Missing forms: ${formValidation.missingForms.join(", ")}. Please complete today's forms.`;
      if (Platform.OS === "web") {
        return alert(msg);
      }
      return Alert.alert("Incomplete Forms", msg, [
        { text: "Open Forms", onPress: () => openForm("Weather") },
        { text: "Cancel" },
      ]);
    }

    if (!formValidation.isWeatherAcceptable) {
      const msg =
        formValidation.weatherValidationMessage ||
        "Weather conditions are not acceptable for cleaning.";
      if (Platform.OS === "web") {
        return alert(msg);
      }
      return Alert.alert("Weather Alert", msg, [
        { text: "Update Form", onPress: () => openForm("Weather") },
        { text: "Continue Anyway" },
      ]);
    }

    navigation.navigate("CleanAndDropSelection");
  }, [formValidation, navigation, openForm]);

  if (!selectedBuilding) {
    return (
      <EmptyState
        title="No building selected"
        message="Please choose a site from the dashboard to continue."
        actionLabel="Back to Home"
        onAction={() => navigation.navigate("Home")}
      />
    );
  }

  if (buildingLoading) {
    return <LoadingSkeleton styles={styles} />;
  }

  if (error) {
    return (
      <EmptyState
        title="Oops!"
        message={error}
        actionLabel="Retry"
        onAction={handleErrorRetry}
      />
    );
  }

  if (!buildingData) {
    return (
      <EmptyState
        title="No data available"
        message="We couldn't resolve building data at this time."
        actionLabel="Retry"
        onAction={handleErrorRetry}
      />
    );
  }

  return (
    <ScrollView
      style={styles.page}
      contentContainerStyle={styles.pageContent}
      keyboardShouldPersistTaps="handled"
    >
      <View style={styles.heroWrap}>
        <BuildingScreenHeader
          name={selectedBuildingName}
          siteId={selectedBuilding.site_id}
          buildingImg={selectedBuilding?.buildingImage}
          address={buildingData?.address}
          isAdhoc={siteIsAdhoc}
          cleanName={currentClean || buildingData?.currentClean}
          progress={progressValue}
          isAdmin={canUpdatePicture}
        />
      </View>

      {!isFullscreen ? (
        <View style={styles.statusRow}>

          <View style={styles.statusCard}>
            <Text style={styles.statusLabel}>Current Status</Text>
            <Text style={styles.statusValue} numberOfLines={1}>
              {currentCleanStatus}
            </Text>
          </View>
        </View>
      ) : null}

      {isWeb ? (
        <View style={[styles.viewerSection, isFullscreen && styles.viewerSectionFullscreen]}>
          {!isFullscreen ? (
            <View style={styles.viewerSectionHeader}>
              <View>
                <Text style={styles.viewerTitle}>3D Building Viewer</Text>
                <Text style={styles.viewerSubtitle}>Inspect drops quickly and verify clean coverage.</Text>
              </View>
              <View style={[styles.viewerModeBadge, lowPerformanceMode && styles.viewerModeBadgeWarning]}>
                <Text style={styles.viewerModeText}>{lowPerformanceMode ? "Low Performance" : "Standard"}</Text>
              </View>
            </View>
          ) : null}

          {!isFullscreen && reason ? <Text style={styles.viewerHelperText}>{reason}</Text> : null}

          <View
            ref={modelViewerRef}
            style={[styles.modelWrapper, isFullscreen && styles.modelWrapperFullscreen]}
            onMouseMove={revealFloatingControls}
            onTouchStart={revealFloatingControls}
          >
            {show3D ? (
              <>
                <Building3D
                  name={selectedBuildingName}
                  siteId={modelSiteId}
                  style={styles.modelFill}
                  isCompact={isCompact}
                  isFullscreen={isFullscreen}
                  onToggleFullscreen={handleToggleFullscreen}
                  drops={drops || []}
                  showStatusColors
                  lowPerformanceMode={lowPerformanceMode}
                  onToggleLowPerformance={handleToggleLowPerformance}
                  showLegend={showLegend}
                  onToggleLegend={handleToggleLegend}
                  onToggle3D={handleToggle3D}
                  isLikelyLowEndDevice={isSamsungA36Like || shouldUseLowPerformanceMode}
                />

                <View
                  style={[
                    styles.floatingControls,
                    !showFloatingControls && styles.floatingControlsHidden,
                  ]}
                  pointerEvents={showFloatingControls ? "auto" : "none"}
                >
                  {isControlsPanelOpen ? (
                    <ScrollView
                      style={styles.floatingPanelScroll}
                      contentContainerStyle={styles.floatingPanelContent}
                      showsVerticalScrollIndicator={false}
                    >
                      <Pressable
                        onPress={handleToggleControlsPanel}
                        style={[styles.floatingButton, styles.floatingButtonSecondary]}
                      >
                        <Text style={styles.floatingButtonText}>Close Controls</Text>
                      </Pressable>

                      <Pressable
                        onPress={handleToggle3D}
                        style={styles.floatingButton}
                        onLongPress={() => showHint("Hide 3D when the phone is hot or battery is low.")}
                      >
                        <EyeIcon />
                        <Text style={styles.floatingButtonText}>Hide 3D</Text>
                      </Pressable>

                      {isFullscreenSupported ? (
                        <Pressable
                          onPress={handleToggleFullscreen}
                          style={styles.floatingButton}
                          onLongPress={() => showHint("Use fullscreen for detailed inspection.")}
                        >
                          {isFullscreen ? <CompressIcon /> : <ExpandIcon />}
                          <Text style={styles.floatingButtonText}>
                            {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                          </Text>
                        </Pressable>
                      ) : null}

                      <Pressable
                        onPress={handleToggleLowPerformance}
                        style={[styles.floatingButton, styles.floatingButtonWarning]}
                        onLongPress={() =>
                          showHint("Use this mode first if 3D is laggy or closes unexpectedly.")
                        }
                      >
                        <BoltIcon />
                        <Text style={styles.floatingButtonText}>
                          {lowPerformanceMode
                            ? "Disable Low Performance Mode"
                            : "Enable Low Performance Mode"}
                        </Text>
                      </Pressable>

                      {/* <Pressable
                        onPress={handleToggleLegend}
                        style={styles.floatingButton}
                        onLongPress={() => showHint("Legend explains what each status color means.")}
                      >
                        <ListIcon />
                        <Text style={styles.floatingButtonText}>
                          {showLegend ? "Hide Legend" : "Show Legend"}
                        </Text>
                      </Pressable> */}
                    </ScrollView>
                  ) : (
                    <Pressable
                      onPress={handleToggleControlsPanel}
                      style={[styles.floatingButton, styles.floatingButtonSecondary]}
                      onLongPress={() => showHint("Open controls to manage 3D options.")}
                    >
                      <ListIcon />
                      <Text style={styles.floatingButtonText}>Open Controls</Text>
                    </Pressable>
                  )}
                </View>
              </>
            ) : (
              <View style={styles.viewerHiddenState}>
                <Animated.View style={{ transform: [{ scale: viewerButtonScale }] }}>
                  <Pressable
                    onPress={handleToggle3D}
                    onPressIn={animateViewerButtonIn}
                    onPressOut={animateViewerButtonOut}
                    style={styles.showViewerButton}
                    onLongPress={() => showHint("Tap once to load the 3D model.")}
                  >
                    <EyeIcon size={16} />
                    <Text style={styles.showViewerButtonText}>Show 3D Viewer</Text>
                  </Pressable>
                </Animated.View>
              </View>
            )}

            {controlHint ? <Text style={styles.controlHintOverlay}>{controlHint}</Text> : null}
          </View>
        </View>
      ) : null}

      {!isFullscreen ? (
        <View style={styles.formsSection}>
          <View style={styles.formsHeaderWrap}>
            <Text style={styles.formsTitle}>Forms & Actions</Text>
            <Text style={styles.formsSubtitle}>
              Clear form status at a glance and open what you need fast.
            </Text>
          </View>

          {groupedForms.map((group) => (
            <View key={group.key} style={styles.formGroupCard}>
              <Text style={styles.formGroupTitle}>{group.title}</Text>
              <Text style={styles.formGroupSubtitle}>{group.subtitle}</Text>

              {group.forms.map((item) => {
                const status = getFormStatus(item);

                return (
                  <Pressable
                    key={item.screen}
                    style={({ pressed }) => [styles.formRow, pressed && styles.formRowPressed]}
                    onPress={() => openForm(item.screen)}
                  >
                    <View style={styles.formRowContent}>
                      <Text style={styles.formRowTitle}>{item.label}</Text>
                      <Text style={styles.formRowSubtitle}>Tap to open form</Text>
                    </View>
                    <View style={styles.formRowActions}>
                      <StatusBadge status={status} styles={styles} />
                      <ChevronRightIcon />
                    </View>
                  </Pressable>
                );
              })}
            </View>
          ))}

          {!siteIsAdhoc ? (
            <View style={styles.cleanNowWrap}>
              <CleanNowCard cleanNow={goToCleanAndDropSelection} />
            </View>
          ) : (
            <Text style={styles.helperText}>
              Ad-hoc sites only require equipment, weather, and PPE forms.
            </Text>
          )}
        </View>
      ) : null}
    </ScrollView>
  );
}

const buildStyles = (width) => {
  const isTablet = width >= 768;
  const isDesktop = width >= 1024;

  return StyleSheet.create({
    page: {
      flex: 1,
      backgroundColor: "#eef3f8",
      width: "100%",
    },
    pageContent: {
      width: "100%",
      maxWidth: 1180,
      alignSelf: "center",
      paddingHorizontal: isDesktop ? 26 : 14,
      paddingTop: 14,
      paddingBottom: 98,
      minHeight: "100%",
    },
    heroWrap: {
      width: "100%",
    },
    statusRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 10,
      marginTop: 12,
      marginBottom: 14,
    },
    statusCard: {
      flexGrow: 1,
      flexBasis: isCompactSize(width) ? "31%" : "32%",
      minHeight: 72,
      borderRadius: 16,
      paddingVertical: 12,
      paddingHorizontal: 12,
      backgroundColor: "#ffffff",
      borderWidth: 1,
      borderColor: "#dce6f2",
      shadowColor: "#0f172a",
      shadowOpacity: 0.05,
      shadowOffset: { width: 0, height: 7 },
      shadowRadius: 16,
      elevation: 2,
    },
    statusLabel: {
      fontSize: 12,
      color: "#64748b",
      marginBottom: 6,
      fontWeight: "700",
    },
    statusValue: {
      fontSize: isTablet ? 17 : 15,
      lineHeight: isTablet ? 22 : 20,
      color: "#0f172a",
      fontWeight: "800",
    },
    viewerSection: {
      width: "100%",
      backgroundColor: "#ffffff",
      borderRadius: 22,
      borderWidth: 1,
      borderColor: "#dbe6f1",
      padding: isTablet ? 16 : 12,
      shadowColor: "#0f172a",
      shadowOpacity: 0.06,
      shadowOffset: { width: 0, height: 10 },
      shadowRadius: 20,
      elevation: 3,
      marginBottom: 14,
    },
    viewerSectionFullscreen: {
      borderRadius: 0,
      borderWidth: 0,
      padding: 0,
      marginBottom: 0,
      shadowOpacity: 0,
      elevation: 0,
      backgroundColor: "#000000",
    },
    viewerSectionHeader: {
      flexDirection: "row",
      justifyContent: "space-between",
      gap: 10,
      marginBottom: 8,
      alignItems: "flex-start",
    },
    viewerTitle: {
      color: "#0f172a",
      fontSize: isTablet ? 20 : 17,
      fontWeight: "800",
      marginBottom: 2,
    },
    viewerSubtitle: {
      color: "#475569",
      fontSize: 13,
      lineHeight: 18,
      maxWidth: "88%",
    },
    viewerModeBadge: {
      backgroundColor: "#dbeafe",
      borderRadius: 999,
      paddingVertical: 6,
      paddingHorizontal: 10,
    },
    viewerModeBadgeWarning: {
      backgroundColor: "#ffedd5",
    },
    viewerModeText: {
      fontSize: 11,
      color: "#1d4ed8",
      fontWeight: "800",
    },
    viewerHelperText: {
      color: "#64748b",
      fontSize: 12,
      lineHeight: 17,
      marginBottom: 8,
    },
    controlHintOverlay: {
      position: "absolute",
      left: 12,
      right: 12,
      bottom: 10,
      color: "#f0f9ff",
      fontSize: 12,
      lineHeight: 16,
      textAlign: "center",
      backgroundColor: "rgba(15, 23, 42, 0.82)",
      borderRadius: 10,
      paddingVertical: 8,
      paddingHorizontal: 10,
    },
    modelWrapper: {
      width: "100%",
      minHeight: isDesktop ? 380 : isTablet ? 320 : 250,
      borderRadius: 18,
      overflow: "hidden",
      backgroundColor: "#f8fafc",
      borderWidth: 1,
      borderColor: "#dbe5ef",
      transitionDuration: "200ms",
      transitionProperty: "all",
      transitionTimingFunction: "ease",
    },
    modelWrapperFullscreen: {
      flex: 1,
      position: "fixed",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      minHeight: "100vh",
      marginTop: 0,
      borderRadius: 0,
      zIndex: 1000,
      backgroundColor: "#000000",
      borderWidth: 0,
    },
    modelFill: {
      flex: 1,
      width: "100%",
      minHeight: isDesktop ? 380 : isTablet ? 320 : 250,
    },
    viewerHiddenState: {
      flex: 1,
      width: "100%",
      minHeight: isDesktop ? 380 : isTablet ? 320 : 250,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#edf3fb",
      paddingHorizontal: 16,
    },
    showViewerButton: {
      minHeight: 54,
      minWidth: isTablet ? 280 : 228,
      paddingHorizontal: 20,
      borderRadius: 14,
      backgroundColor: "#0f766e",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      shadowColor: "#0f766e",
      shadowOpacity: 0.24,
      shadowOffset: { width: 0, height: 8 },
      shadowRadius: 16,
      elevation: 4,
    },
    showViewerButtonText: {
      color: "#ffffff",
      fontSize: 15,
      fontWeight: "800",
      letterSpacing: 0.2,
    },
    floatingControls: {
      position: "absolute",
      top: isDesktop ? 14 : 12,
      right: isDesktop ? 14 : 8,
      zIndex: 1400,
      alignItems: "flex-end",
      opacity: 1,
      transitionDuration: "180ms",
      transitionProperty: "opacity",
      transitionTimingFunction: "ease",
    },
    floatingControlsHidden: {
      opacity: 0,
    },
    floatingPanelScroll: {
      maxHeight: isDesktop ? 400 : isTablet ? 340 : 305,
      minWidth: isTablet ? 250 : 220,
      borderRadius: 14,
      backgroundColor: "rgba(15, 23, 42, 0.78)",
      borderWidth: 1,
      borderColor: "rgba(226, 232, 240, 0.22)",
      paddingVertical: 8,
      paddingHorizontal: 8,
    },
    floatingPanelContent: {
      paddingBottom: 2,
    },
    floatingButton: {
      minHeight: 48,
      borderRadius: 12,
      marginBottom: 8,
      paddingHorizontal: 12,
      backgroundColor: "rgba(30, 41, 59, 0.92)",
      borderWidth: 1,
      borderColor: "rgba(226, 232, 240, 0.24)",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: 8,
    },
    floatingButtonWarning: {
      backgroundColor: "rgba(146, 64, 14, 0.92)",
      borderColor: "rgba(251, 191, 36, 0.4)",
    },
    floatingButtonSecondary: {
      backgroundColor: "rgba(14, 116, 144, 0.9)",
      borderColor: "rgba(125, 211, 252, 0.4)",
    },
    floatingButtonText: {
      color: "#f8fafc",
      fontSize: 13,
      fontWeight: "800",
      letterSpacing: 0.2,
      flexShrink: 1,
    },
    formsSection: {
      width: "100%",
      marginTop: 2,
    },
    formsHeaderWrap: {
      marginBottom: 10,
      paddingHorizontal: 2,
    },
    formsTitle: {
      color: "#0f172a",
      fontSize: isTablet ? 22 : 19,
      fontWeight: "800",
      marginBottom: 2,
    },
    formsSubtitle: {
      color: "#64748b",
      fontSize: 13,
      lineHeight: 18,
    },
    formGroupCard: {
      backgroundColor: "#ffffff",
      borderRadius: 18,
      borderWidth: 1,
      borderColor: "#dbe5ef",
      paddingHorizontal: 12,
      paddingTop: 12,
      paddingBottom: 4,
      marginBottom: 10,
      shadowColor: "#0f172a",
      shadowOpacity: 0.05,
      shadowOffset: { width: 0, height: 8 },
      shadowRadius: 16,
      elevation: 2,
    },
    formGroupTitle: {
      fontSize: 16,
      color: "#0f172a",
      fontWeight: "800",
      marginBottom: 2,
      paddingHorizontal: 4,
    },
    formGroupSubtitle: {
      fontSize: 12,
      color: "#64748b",
      lineHeight: 17,
      marginBottom: 10,
      paddingHorizontal: 4,
    },
    formRow: {
      minHeight: 66,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: "#e2e8f0",
      backgroundColor: "#f8fbff",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 12,
      marginBottom: 8,
    },
    formRowPressed: {
      backgroundColor: "#eef4fb",
      borderColor: "#c8d7e8",
    },
    formRowContent: {
      flex: 1,
      marginRight: 8,
    },
    formRowTitle: {
      color: "#0f172a",
      fontSize: 15,
      lineHeight: 20,
      fontWeight: "700",
    },
    formRowSubtitle: {
      color: "#64748b",
      fontSize: 12,
      marginTop: 2,
    },
    formRowActions: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
    },
    formStatusBadge: {
      borderRadius: 999,
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderWidth: 1,
    },
    formStatusBadgeComplete: {
      backgroundColor: "#dcfce7",
      borderColor: "#86efac",
    },
    formStatusBadgePending: {
      backgroundColor: "#fff7ed",
      borderColor: "#fdba74",
    },
    formStatusBadgeUnknown: {
      backgroundColor: "#e2e8f0",
      borderColor: "#cbd5e1",
    },
    formStatusText: {
      fontSize: 11,
      fontWeight: "800",
    },
    formStatusTextComplete: {
      color: "#166534",
    },
    formStatusTextPending: {
      color: "#9a3412",
    },
    formStatusTextUnknown: {
      color: "#334155",
    },
    cleanNowWrap: {
      marginTop: 6,
      marginBottom: 8,
    },
    helperText: {
      fontSize: 13,
      color: "#64748b",
      lineHeight: 19,
      paddingHorizontal: 2,
      marginTop: 6,
    },
    loadingContainer: {
      flex: 1,
      backgroundColor: "#eef3f8",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      minHeight: "100vh",
      padding: 20,
    },
    skeletonCard: {
      width: "100%",
      minHeight: 100,
      borderRadius: 20,
      backgroundColor: "#dbe5ef",
      marginBottom: 14,
    },
  });
};

const isCompactSize = (width) => width < 420;
